import { Inject, Injectable } from '@angular/core';
import { combineLatest, from, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, pairwise, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@shared/localization';
import {
  RetrieveResourceDetailErrorCode,
  RetrieveResourceDetailException,
  SaveResourceErrorCode,
  SaveResourceException,
} from '@protocols/use-cases';

import { ResourceEditorEffects } from '../../../effects/resource-editor.effects';
import { ResourceEditorState } from '../../../state/resource-editor.state';
import { ResourceDraftData, ResourceDraftOverride, StagedResourcesState } from '../../../state/staged-resources.state';

/**
 * Shape of the unified error stream the facade emits to the smart component.
 * The `key` is an i18n key; `params` is optional placeholder substitution
 * passed straight to `| translate: error.params` in the template. File-local
 * because only this facade and its own template need to know it. Mirrors the
 * pipeline editor's `ErrorMessage`.
 */
type ErrorMessage = { key: string; params?: Record<string, unknown> };

@Injectable()
export class ResourceEditorFacade {
  constructor(
    private readonly editorEffects: ResourceEditorEffects,
    private readonly state: ResourceEditorState,
    private readonly stagedState: StagedResourcesState,
    @Inject(SUPPORTED_LANGUAGES) readonly supportedLanguages: SupportedLanguage[]
  ) {}

  // ============================================================================
  // Selectors — passthrough
  // ============================================================================

  public readonly id$ = this.state.id$;
  public readonly containerId$ = this.state.containerId$;
  public readonly loading$ = this.state.loading$;
  public readonly typeLocked$ = this.state.typeLocked$;
  public readonly name$ = this.state.name$;
  public readonly type$ = this.state.type$;
  public readonly version$ = this.state.version$;
  public readonly description$ = this.state.description$;
  public readonly value$ = this.state.value$;
  public readonly file$ = this.state.file$;
  public readonly label$ = this.state.label$;
  public readonly coordinates$ = this.state.coordinates$;
  public readonly displayName$ = this.state.displayName$;

  // ============================================================================
  // Selectors — UI-shaped derivations
  // ============================================================================

  /**
   * In-flight signal for any save reaching the editor. The save cascade flips
   * the staged-resources `saving` flag for the whole bottom-up walk — including
   * the root resource this editor owns — so there is no separate editor-side
   * saving slot to combine.
   */
  public readonly saving$: Observable<boolean> = this.stagedState.saving$;

  /** True while either the initial load or any save phase is in flight. */
  public readonly busy$: Observable<boolean> = combineLatest([this.loading$, this.saving$]).pipe(
    map(([loading, saving]) => loading || saving),
    distinctUntilChanged()
  );

  /**
   * Whether cancelling now would discard user work — root fields, root child
   * assignments, or staged nested-session rows. Compares the live session
   * signature (`ResourceEditorEffects.currentSignature$`) against the baseline
   * captured at seed. False until a seed completes (`baselineSignature` still
   * `null`), so a failed load never warns.
   */
  public readonly hasChanges$: Observable<boolean> = this.state.select(
    this.editorEffects.currentSignature$,
    this.state.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  /**
   * Gates the embedded resource manager. The legacy editor showed the
   * mask-results assignment block only for masks; requiring a `containerId`
   * guarantees the tree root is already seeded before the manager's
   * `RESOURCE_MANAGER_CONTAINER_ID` resolves against it.
   */
  public readonly showManager$: Observable<boolean> = combineLatest([this.type$, this.containerId$]).pipe(
    map(([type, containerId]) => type === 'mask' && !!containerId),
    distinctUntilChanged()
  );

  /**
   * Unified error stream for the banner. Precedence: editor-owned UI key >
   * load exception > the ROOT resource's save exception. Child-row save
   * failures surface as snackbars via `childSaveFailed$`, not here. Effects
   * never write i18n keys — they push typed exceptions through state and the
   * facade dispatches into its own namespace.
   */
  public readonly error$: Observable<ErrorMessage | null> = combineLatest([
    this.state.error$,
    this.state.loadException$,
    this.rootSaveException$(),
  ]).pipe(
    map(([editorKey, loadEx, saveEx]) => {
      if (editorKey) return { key: editorKey };
      if (loadEx) return this.loadErrorToMessage(loadEx);
      if (saveEx) return this.saveErrorToMessage(saveEx);

      return null;
    }),
    distinctUntilChanged((a, b) => a?.key === b?.key && JSON.stringify(a?.params) === JSON.stringify(b?.params))
  );

  /**
   * The root resource's save exception, read out of the cascade's per-row
   * exception map keyed by the editor's `containerId`. The root is just another
   * ledger entry (the deepest parent persists last), so its failure lands in the
   * same map as the children — keyed by the container id this editor owns.
   */
  private rootSaveException$(): Observable<Error | null> {
    return combineLatest([this.containerId$, this.stagedState.saveExceptions$]).pipe(
      map(([containerId, exceptions]) => (containerId ? exceptions[containerId] ?? null : null))
    );
  }

  private loadErrorToMessage(error: Error): ErrorMessage {
    if (error instanceof RetrieveResourceDetailException) {
      switch (error.code) {
        case RetrieveResourceDetailErrorCode.RESOURCE_NOT_FOUND:
          return { key: 'resource_editor.errors.resource_not_found' };
        case RetrieveResourceDetailErrorCode.REPOSITORY_ERROR:
          return { key: 'resource_editor.errors.repository_error' };
      }
    }
    return { key: 'resource_editor.errors.load_failed' };
  }

  private saveErrorToMessage(error: Error): ErrorMessage {
    if (error instanceof SaveResourceException) {
      switch (error.code) {
        case SaveResourceErrorCode.MISSING_NAME:
          return { key: 'resource_editor.errors.missing_name' };
        case SaveResourceErrorCode.MISSING_TYPE:
          return { key: 'resource_editor.errors.missing_type' };
        case SaveResourceErrorCode.UPLOAD_FAILED:
          return { key: 'resource_editor.errors.upload_failed' };
        case SaveResourceErrorCode.MISSING_PERSISTED_ID:
          return { key: 'resource_editor.errors.missing_persisted_id' };
      }
    }
    return { key: 'resource_editor.errors.save_failed' };
  }

  // ============================================================================
  // childSaveFailed$ — per-row snackbar dispatch stream
  // ============================================================================

  /**
   * Index every staged draft by its in-memory id across both ledger slots so a
   * failing child row can be resolved to a display name. Overrides are keyed
   * under their id, and the override half carries the user-facing (edited) name.
   */
  private readonly _draftNameById$: Observable<ReadonlyMap<string, string>> = combineLatest([
    this.stagedState.pendingResources$,
    this.stagedState.resourceOverrides$,
  ]).pipe(
    map(([pending, overrides]: [ResourceDraftData[], Record<string, ResourceDraftOverride>]) => {
      const byId = new Map<string, string>();
      for (const draft of pending) byId.set(draft.id, draft.name);
      for (const id of Object.keys(overrides)) byId.set(id, overrides[id].override.name);
      return byId;
    })
  );

  /**
   * Emit one event per id that newly appears in (or changes within) the staged
   * `saveExceptions` map. The cascade clears the map at the start of each
   * attempt, so a fresh failure is always a new key — this turns the error
   * *state* back into per-row *events* the component can toast, without
   * re-emitting failures already shown. Mirrors the pipeline facade's
   * `newErrors$` idiom.
   */
  private newErrors$(source$: Observable<Record<string, Error>>): Observable<{ id: string; error: Error }> {
    return source$.pipe(
      startWith({} as Record<string, Error>),
      pairwise(),
      map(([prev, curr]) =>
        Object.keys(curr)
          .filter((id) => curr[id] !== prev[id])
          .map((id) => ({ id, error: curr[id] }))
      ),
      filter((added) => added.length > 0),
      mergeMap((added) => from(added))
    );
  }

  /**
   * Each failing NON-root resource save emits a `{ name }` the smart component
   * dispatches into a snackbar. The root container is excluded — its failure is
   * the banner's job (see `error$`). An empty `name` means the lookup missed
   * (defensive; should not happen in normal flows).
   */
  public readonly childSaveFailed$: Observable<{ name: string }> = this.newErrors$(
    this.stagedState.saveExceptions$
  ).pipe(
    withLatestFrom(this.containerId$, this._draftNameById$),
    filter(([{ id }, containerId]) => id !== containerId),
    map(([{ id }, , lookup]) => ({ name: lookup.get(id) ?? '' }))
  );

  // ============================================================================
  // Effects + updaters — passthrough
  // ============================================================================

  /** Triggered by the component on init — I/O is driven by lifecycle, not DI. */
  public readonly loadResource = this.editorEffects.loadResource;

  /**
   * Save is a pure handoff: it does NOT persist. It emits the one-shot `saved`
   * outcome on the shared editor effects; the draft is already available on the
   * shared state as `draft$`. The consumer owns persistence (page facade runs
   * the cascade, dialog stages the draft). Copied verbatim from the pipeline
   * editor.
   */
  public readonly save = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.editorEffects.confirm({ kind: 'saved' })))
  );

  /**
   * Cancel is a pure handoff too: emit the one-shot `cancelled` outcome on the
   * shared editor effects. The consumer decides what to do — navigate back,
   * close the dialog, discard the draft.
   */
  public readonly cancel = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.editorEffects.confirm({ kind: 'cancelled' })))
  );

  public readonly setName = this.state.setName;
  public readonly setVersion = this.state.setVersion;
  public readonly setDescription = this.state.setDescription;
  public readonly setLabel = this.state.setLabel;
  public readonly setType = this.state.setType;
  public readonly setCoordinates = this.state.setCoordinates;
  public readonly updateFile = this.state.setFile;

  /**
   * The typed `ts-language-input` emits `Partial<{ [lang: string]: string }>` —
   * disabled controls drop out. The state expects a complete record (one entry
   * per supported language), so the facade fills the missing keys with empty
   * strings before mutating state. Mirrors the pipeline facade's
   * `toFullLangMap` idiom.
   */
  public readonly setDisplayName = this.state.effect(
    (displayName$: Observable<Partial<{ [lang: string]: string }>>) =>
      displayName$.pipe(
        tap((partial: Partial<{ [lang: string]: string }>) => this.state.setDisplayName(this.toFullLangMap(partial)))
      )
  );

  private toFullLangMap(partial: Partial<{ [lang: string]: string }>): { [lang: string]: string } {
    return Object.fromEntries(this.supportedLanguages.map((lang) => [lang.value, partial[lang.value] ?? '']));
  }
}
